import { c as _c } from "react/compiler-runtime";
import { useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import useSession from '@/hooks/use-session';
import { useChatSessionMessages } from '../../hooks/use-chat-session-messages';
import { useInvestigationChatSession } from '../../hooks/use-investigation-chat-session';
import { mapChatSessionMessages } from '../../utils/map-chat-session-messages';
import ChatMessagesSkeleton from '../chat-messages-skeleton';
import ChatPanel from '../chat-panel';
import { PASSIVE_SESSION } from '../chat-session';
import { type BackgroundAgent, deriveBackgroundAgents } from '../chat-widget/background-agents-context';
import { AgentTranscript } from '../chat-widget/background-agents-panel';

// The read-only transcript grows to its natural height and the host (the dialog) scrolls it.
const getScrollParent = (el: HTMLElement | null): HTMLElement | null => {
  for (let node = el?.parentElement ?? null; node; node = node.parentElement) {
    const {
      overflowY
    } = getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll') return node;
  }
  return null;
};
type InvestigationTranscriptProps = {
  fpId: string;
  investigationId: string;
  /** The sub-agent whose transcript is shown in place of the run's. */
  selectedAgentId?: string | null;
  onOpenAgent?: (agent: BackgroundAgent) => void;
};

// Read-only view of an investigation's agent-run chat, rendered inside the header's transcript
// modal. This is the only place the run's messages are fetched (see useInvestigationChatSession).
const InvestigationTranscript = t0 => {
  const $ = _c(15);
  const {
    fpId,
    investigationId,
    selectedAgentId,
    onOpenAgent
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "floatingChat.transcript"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  const {
    isLoading: sessionLoading,
    chatSessionId
  } = useInvestigationChatSession(fpId, investigationId);
  const {
    isLive
  } = useSession();
  const rootRef = useRef(null);
  const savedScrollTopRef = useRef(null);
  const {
    messages: persisted,
    isLoading: messagesLoading
  } = useChatSessionMessages(chatSessionId, isLive);
  const messages = mapChatSessionMessages(persisted);
  let t2;
  let t3;
  if ($[1] !== selectedAgentId) {
    t2 = () => {
      const scroller = getScrollParent(rootRef.current);
      if (!scroller) {
        return;
      }
      if (selectedAgentId) {
        scroller.scrollTop = 0;
      } else {
        if (savedScrollTopRef.current !== null) {
          scroller.scrollTop = savedScrollTopRef.current;
          savedScrollTopRef.current = null;
        }
      }
    };
    t3 = [selectedAgentId];
    $[1] = selectedAgentId;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useLayoutEffect(t2, t3);
  const isLoading = sessionLoading || !!chatSessionId && (isLive === undefined || messagesLoading);
  if (isLoading) {
    let t4;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <ChatMessagesSkeleton readOnly={true} />;
      $[4] = t4;
    } else {
      t4 = $[4];
    }
    return t4;
  }
  if (!chatSessionId || messages.length === 0) {
    let t4;
    if ($[5] !== t) {
      t4 = t(_temp);
      $[5] = t;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    let t5;
    if ($[7] !== t4) {
      t5 = <div className="flex min-h-[calc(100vh-100px)] items-center justify-center px-6 text-center"><p className="text-body-3 text-tertiary">{t4}</p></div>;
      $[7] = t4;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    return t5;
  }
  const agents = deriveBackgroundAgents(messages).items;
  const openAgent = spawnId => {
    const agent = agents.find(candidate => candidate.id === spawnId);
    if (!agent) {
      return;
    }
    if (!selectedAgentId) {
      savedScrollTopRef.current = getScrollParent(rootRef.current)?.scrollTop ?? null;
    }
    onOpenAgent?.(agent);
  };
  const selected = selectedAgentId ? agents.find(agent_0 => agent_0.id === selectedAgentId) : undefined;
  if (selected) {
    let t4;
    if ($[9] !== openAgent || $[10] !== selected) {
      t4 = <div ref={rootRef}><AgentTranscript key={selected.id} agent={selected} onOpenAgent={openAgent} /></div>;
      $[9] = openAgent;
      $[10] = selected;
      $[11] = t4;
    } else {
      t4 = $[11];
    }
    return t4;
  }
  let t4;
  if ($[12] !== messages || $[13] !== openAgent) {
    t4 = <div ref={rootRef}><ChatPanel readOnly={true} messages={messages} session={PASSIVE_SESSION} isConnected={false} isThinking={false} onSendMessage={_temp2} onInterrupt={_temp3} onOpenAgent={openAgent} /></div>;
    $[12] = messages;
    $[13] = openAgent;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
};
export default InvestigationTranscript;
function _temp($) {
  return $.empty;
}
async function _temp2() {}
function _temp3() {}